import { useState, useEffect } from 'react'
import { Users, Briefcase, TrendingUp, AlertTriangle, Activity } from 'lucide-react'
import { getAllUsers } from '../../services/auth.service'
import { getClients } from '../../services/clients.service'
import { getInvoices } from '../../services/invoices.service'
import { getProjects } from '../../services/projects.service'
import type { User } from '../../types/auth.types'
import type { Client, Invoice, Project } from '../../types'

function formatMRR(n: number) {
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + 'Cr'
  if (n >= 100000)   return '₹' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000)     return '₹' + (n / 1000).toFixed(0) + 'k'
  return '₹' + n
}

function StatCard({ label, value, sub, color, icon: Icon }: {
  label: string; value: string; sub: string; color: string; icon: React.ElementType
}) {
  return (
    <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <p className="text-xs font-medium" style={{ color: '#a0a0a0' }}>{label}</p>
      </div>
      <p className="text-2xl font-bold text-white mb-0.5" style={{ fontFamily: 'Syne, sans-serif' }}>{value}</p>
      <p className="text-xs" style={{ color: '#555' }}>{sub}</p>
    </div>
  )
}

const FOUNDER_META = [
  { founderKey: 'bunny',  name: 'Bunny',  label: 'Production',  color: '#ffc600', modules: ['Content Calendar', 'Scheduling', 'Projects', 'Campaigns (view)'] },
  { founderKey: 'hj',     name: 'HJ',     label: 'Sales & Ops', color: '#e8a000', modules: ['Pipeline', 'Invoices', 'Expenses', 'Clients'] },
  { founderKey: 'darshu', name: 'Darshu', label: 'Paid Ads',    color: '#ff8c00', modules: ['Campaigns', 'Ad Performance', 'Budget Tracker'] },
]

export default function AdminOverview() {
  const [users,    setUsers]    = useState<User[]>([])
  const [clients,  setClients]  = useState<Client[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    Promise.all([
      getAllUsers(),
      getClients(),
      getInvoices(),
      getProjects(),
    ]).then(([u, c, inv, proj]) => {
      setUsers(u)
      setClients(c)
      setInvoices(inv)
      setProjects(proj)
    }).catch(console.error).finally(() => setLoading(false))
  }, [])

  const employees = users.filter(u => u.role === 'employee')
  const founders  = users.filter(u => u.role === 'founder')

  // Computed stats
  const activeClients = clients.filter(c => c.status === 'active')
  const totalMRR      = activeClients.reduce((s, c) => s + c.mrr, 0)

  const now = new Date()
  const newClientsThisMonth = clients.filter(c => {
    const d = new Date(c.joinedAt)
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  }).length

  const activeProjects    = projects.filter(p => p.status !== 'done').length
  const overdueInvoices   = invoices.filter(i => i.status === 'overdue')
  const overdueAmount     = overdueInvoices.reduce((s, i) => s + i.total, 0)

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Admin Overview</h1>
        <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>Full system visibility — all layers</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total MRR"
          value={loading ? '—' : formatMRR(totalMRR)}
          sub={loading ? 'Loading…' : `${activeClients.length} active clients`}
          color="#ffc600"
          icon={TrendingUp}
        />
        <StatCard
          label="Active Clients"
          value={loading ? '—' : `${activeClients.length}`}
          sub={loading ? 'Loading…' : `${newClientsThisMonth} new this month`}
          color="#e8a000"
          icon={Users}
        />
        <StatCard
          label="Active Projects"
          value={loading ? '—' : `${activeProjects}`}
          sub={loading ? 'Loading…' : `${projects.filter(p => p.status === 'done').length} completed`}
          color="#ffc600"
          icon={Briefcase}
        />
        <StatCard
          label="Overdue Invoices"
          value={loading ? '—' : formatMRR(overdueAmount)}
          sub={loading ? 'Loading…' : `${overdueInvoices.length} invoice${overdueInvoices.length !== 1 ? 's' : ''}`}
          color="#cc3300"
          icon={AlertTriangle}
        />
      </div>

      {/* Founder panels */}
      <div>
        <h2 className="text-sm font-bold text-white mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>Founder Panels</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FOUNDER_META.map(f => {
            const founder = founders.find(u => u.founderKey === f.founderKey)
            const founderEmployees = employees.filter(e => e.reportingTo === (founder?.id ?? f.founderKey))
            return (
              <div key={f.founderKey} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: `1px solid ${f.color}25` }}>
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                    style={{ background: f.color, color: '#000' }}>
                    {founder?.avatarInitials ?? f.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{f.name}</p>
                    <p className="text-xs" style={{ color: '#a0a0a0' }}>{f.label}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#e8a000' }} />
                    <span className="text-xs" style={{ color: '#a0a0a0' }}>Active</span>
                  </div>
                </div>

                {/* Modules */}
                <div className="mb-4">
                  <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>MODULE ACCESS</p>
                  <div className="flex flex-wrap gap-1.5">
                    {f.modules.map(m => (
                      <span key={m} className="text-xs px-2 py-0.5 rounded" style={{ background: `${f.color}12`, color: f.color }}>
                        {m}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Employees */}
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>TEAM ({founderEmployees.length})</p>
                  <div className="space-y-1.5">
                    {founderEmployees.length === 0 ? (
                      <p className="text-xs" style={{ color: '#333' }}>No employees assigned</p>
                    ) : founderEmployees.map(emp => (
                      <div key={emp.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: '#1a1a1a' }}>
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{ background: f.color, color: '#000', fontSize: '9px' }}>
                          {emp.avatarInitials}
                        </div>
                        <span className="text-xs text-white">{emp.name}</span>
                        <div className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#e8a000' }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* System status */}
      <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Activity size={15} style={{ color: '#ffc600' }} />
          <h2 className="text-sm font-bold text-white">System Status</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Users',    value: users.length },
            { label: 'Founders',       value: founders.length },
            { label: 'Employees',      value: employees.length },
            { label: 'Active Sessions', value: 1 },
          ].map(({ label, value }) => (
            <div key={label} className="px-3 py-2 rounded-lg text-center" style={{ background: '#1a1a1a' }}>
              <p className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{value}</p>
              <p className="text-xs" style={{ color: '#555' }}>{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
